import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { __ } from '@wordpress/i18n';
import { smashBlocks } from "../blocks";
import FeedCTA from '../cta';

const elementorWidgets = smashBlocks.map((block) => ({
    blockId: block.id,
    name: `smash-${block.id}-feed`,
    title: block.title,
    icon: `eicon-${block.id === 'reviews' ? 'review' : block.id}`,
    categories: ['smashballoon'],
    customizerPage: block.customizerPage,
}));

const getElementorWidgetConfig = (blockId) => {
    return elementorWidgets.find((w) => w.blockId === blockId) || null;
};

const addEditorPreviewOverlay = (container) => {
    if (!container) return;
    if (container.querySelector('#sbc-elementor-cta-root')) return;

    const target = container.querySelector('.elementor-widget-container') || container;

    // Elementor may re-render the widget's inner DOM on settings change while
    // keeping the outer container. The overlay element gets torn down but the
    // idempotency flag persists, so reset it when the previous overlay is gone.
    if (container.dataset.sbcOverlayInjected && !target.querySelector('.sbc-elementor-preview-overlay')) {
        delete container.dataset.sbcOverlayInjected;
    }
    if (container.dataset.sbcOverlayInjected) return;

    if (getComputedStyle(target).position === 'static') {
        target.style.position = 'relative';
    }

    const overlay = document.createElement('div');
    overlay.className = 'sbc-elementor-preview-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    // High z-index ensures the overlay sits above Elementor's preview stacking context and any feed-internal layers (lightbox triggers, sticky elements) so editor clicks never reach the feed.
    overlay.style.cssText = 'position:absolute;inset:0;z-index:999;cursor:default;';
    target.appendChild(overlay);
    container.dataset.sbcOverlayInjected = 'true';
};

const elementorPreviewInit = (widgetName, initCallback) => {
    if (
        typeof window !== 'undefined' &&
        window.elementorFrontend &&
        window.elementorFrontend.hooks
    ) {
        window.elementorFrontend.hooks.addAction(
            `frontend/element_ready/${widgetName}.default`,
            ($scope) => {
                const container = $scope?.[0] || $scope;
                if (typeof initCallback === 'function') {
                    initCallback(container);
                }
            }
        );
    }
};

const reorderPlaceholderOption = (panel) => {
    const $select = panel.$el.find('.elementor-control-feed_id select');
    if (!$select.length) return;

    const $placeholder = $select.find('option[value=""]');
    if (!$placeholder.length) return;
    if ($select.children().first().attr('value') === '') return;

    const currentVal = $select.val();
    $select.prepend($placeholder);
    if (currentVal) $select.val(currentVal);
};

const injectFeedLinks = (panel, feedUrl) => {
    const $control = panel.$el.find('.elementor-control-feed_id');
    if (!$control.length || !feedUrl) return;

    const parentJQuery = window.parent?.jQuery;
    if (!parentJQuery) return;

    const $field = $control.find('.elementor-control-field');
    const $wrapper = $field.find('.elementor-control-input-wrapper');

    // Remove existing links to avoid duplicates on panel reopen
    $field.find('.sbc-elementor-feed-links').remove();

    const $select = $control.find('select');
    const selectedValue = $select.val();

    const editDisplay = selectedValue ? '' : 'display:none;';
    const separatorDisplay = selectedValue ? 'inline-block' : 'none';

    const $links = parentJQuery(
        '<div class="sbc-elementor-feed-links" style="font-weight:700; color:#a73061; margin-top:10px;">' +
        '<a class="sbc-edit-feed-link" href="' + feedUrl + '&feed_id=' + selectedValue + '" target="_blank" style="color:#a73061;' + editDisplay + '">' + __('Edit this Feed', 'sb-common') + '</a>' +
        '<span class="sbc-feed-links-separator" style="color:#aaa; display:' + separatorDisplay + '; margin:0 5px;">|</span>' +
        '<a class="sbc-create-feed-link" href="' + feedUrl + '" target="_blank" style="color:#a73061;">' + __('Create New Feed', 'sb-common') + '</a>' +
        '</div>'
    );

    $wrapper.after($links);

    // Update edit link visibility and URL on feed selection change
    $select.off('change.sbcFeedLinks').on('change.sbcFeedLinks', function () {
        const val = this.value;
        const $editLink = $field.find('.sbc-edit-feed-link');
        const $separator = $field.find('.sbc-feed-links-separator');

        if (val) {
            $editLink.attr('href', feedUrl + '&feed_id=' + val).show();
            $separator.css('display', 'inline-block');
        } else {
            $editLink.hide();
            $separator.hide();
        }
    });
};

// CFF Pro only: in Chromium's Elementor preview iframe, the embedded
// masonry plugin fails to register items and leaves .cff-posts-wrap at
// height:0 while items end up position:absolute via CSS — overlapping
// each other and the Load-more button. Force items to natural flow and
// pin a min-height on the wrap so the preview stays usable. Activated
// only when the host plugin opts in via `cffChromiumMasonryFix: true`,
// and the CFF-specific selectors ensure it's a no-op for other feeds.
const applyCffChromiumMasonryFix = () => {
    if (!window.chrome) return;
    const wraps = document.querySelectorAll('.cff-masonry-js .cff-posts-wrap');
    wraps.forEach((wrap) => {
        wrap.style.height = 'auto';
        wrap.style.minHeight = '500px';
        wrap.style.overflow = 'hidden';
    });
    const items = document.querySelectorAll(
        '.cff-masonry-js .cff-album-item, .cff-masonry-js .cff-item, .cff-masonry-js .cff-likebox'
    );
    items.forEach((item) => {
        item.style.position = 'static';
        item.style.left = 'auto';
        item.style.top = 'auto';
    });
};

const initElementorFeedWidget = ({
    blockId,
    widgetName,
    globalVar,
    feedInitFn,
    ctaRootId = 'sbc-elementor-cta-root',
    cffChromiumMasonryFix = false,
}) => {
    const mountCTA = (container) => {
        const {
            feeds = [],
            feed_url: feedUrl = '',
            is_pro_active: isPro = false,
        } = window[globalVar] || {};

        const feedsOptions = [
            { label: __('Select a Feed', 'sb-common'), value: '' },
            ...feeds.map((feed) => ({
                label: feed.feed_name || `Feed ${feed.id}`,
                value: String(feed.id),
            })),
        ];

        const setAttributes = (attrs) => {
            if (attrs.feedId) {
                const parentJQuery = window.parent?.jQuery;
                if (parentJQuery) {
                    parentJQuery('#elementor-controls select[data-setting="feed_id"]')
                        .val(attrs.feedId)
                        .trigger('change');
                }
            }
        };

        const root = createRoot(container);
        root.render(
            createElement(FeedCTA, {
                id: blockId,
                feeds,
                feedsOptions,
                createFeedUrl: feedUrl,
                isPro,
                attributes: { feedId: '' },
                setAttributes,
            })
        );
    };

    const initWidget = () => {
        elementorPreviewInit(widgetName, (container) => {
            if (typeof feedInitFn === 'function') {
                feedInitFn();
            }
            if (cffChromiumMasonryFix) {
                applyCffChromiumMasonryFix();
            }
            const ctaRoot = container.querySelector?.(`#${ctaRootId}`);
            if (ctaRoot) {
                mountCTA(ctaRoot);
            }
            // Cover the rendered feed with a transparent overlay inside the
            // editor preview so clicks select the widget instead of triggering
            // lightboxes, Load More, link navigation, etc. Frontend is untouched.
            if (!window.elementorFrontend?.isEditMode?.()) return;

            addEditorPreviewOverlay(container);

            // Elementor only fires `frontend/element_ready` on initial widget
            // mount, not on settings-driven re-renders. Watch the container
            // so the overlay gets (re-)added when the cta-root is swapped out
            // for the rendered feed.
            if (container.dataset.sbcOverlayObserved) return;
            container.dataset.sbcOverlayObserved = 'true';

            const observer = new MutationObserver(() => addEditorPreviewOverlay(container));
            observer.observe(container, { childList: true, subtree: true });

            // Stop observing once the widget is removed from the DOM.
            const doc = container.ownerDocument || document;
            const root = doc.body || doc.documentElement;
            const cleanup = new MutationObserver(() => {
                if (!doc.contains(container)) {
                    observer.disconnect();
                    cleanup.disconnect();
                }
            });
            if (root) {
                cleanup.observe(root, { childList: true, subtree: true });
            }
        });
    };

    if (window.elementorFrontend?.hooks) {
        initWidget();
    } else {
        jQuery(window).on('elementor/frontend/init', initWidget);
    }

    const registerPanelHook = () => {
        if (!window.parent?.elementor?.hooks) return;
        window.parent.elementor.hooks.addAction(
            `panel/open_editor/widget/${widgetName}`,
            (panel) => {
                const { feed_url: feedUrl = '' } = window[globalVar] || {};

                reorderPlaceholderOption(panel);
                injectFeedLinks(panel, feedUrl);

                panel.$el
                    .find('.elementor-control-feed_id select')
                    .off('change.sbcFeedInit')
                    .on('change.sbcFeedInit', () => {
                        setTimeout(() => {
                            if (typeof feedInitFn === 'function') {
                                feedInitFn();
                            }
                            if (cffChromiumMasonryFix) {
                                applyCffChromiumMasonryFix();
                            }
                            // Defense-in-depth: the MutationObserver in
                            // initWidget should catch the re-render, but
                            // re-trigger explicitly in case it didn't settle.
                            const previewWin = window.elementor?.$preview?.[0]?.contentWindow;
                            const widget = previewWin?.document?.querySelector(
                                `[data-widget_type="${widgetName}.default"]`
                            );
                            if (widget && previewWin?.elementorFrontend?.isEditMode?.()) {
                                addEditorPreviewOverlay(widget);
                            }
                        }, 4000);
                    });
            }
        );
    };

    if (window.parent?.elementor?.hooks) {
        registerPanelHook();
    } else {
        jQuery(window).on('elementor/frontend/init', registerPanelHook);
    }
};

export { elementorWidgets, getElementorWidgetConfig, elementorPreviewInit, initElementorFeedWidget };
